// ============================================================
// Die schwebende Kopfleiste (.design/build.mjs `topnav`).
//
// EINE Leiste mit ZWEI Fassungen, nicht zwei Leisten:
//   angemeldet   Marke · Reiter · Suche · DE|EN · Thema · Konto
//   abgemeldet   Marke · DE|EN · Thema · „Anmelden"     (E21, Schaufenster)
// Welche gilt, entscheidet der Anmeldestatus — kein Aufrufer muss es wissen.
//
// Sie SCHWEBT über allem (`position: fixed`, 16 px Luft ringsum, 56 px hoch,
// Radius 18, Glas). Die Karte reicht darunter bis an den oberen Rand; jeder
// andere Bildschirm rechnet seinen Freiraum aus `--kopf-frei` in base.css und
// nicht aus einer eigenen Zahl.
//
// Die Karte (F2) zeichnet die Leiste SELBST, weil sie den ganzen Schirm füllt
// und keine `.huelle` hat. Deshalb ist diese Komponente auch für F2 exportiert
// und hängt an nichts, was nur `Shell` wüsste.
// ============================================================
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { Avatar, Button, Icon, IconButton } from "../ui";
import { Logo } from "./Logo";
import { Suchliste } from "./Suchliste";
import { freigabenZahl, useFreigaben } from "../../api/freigaben";
import { schluessel, useIch, useIchAendern, useMeineRunde, useNeuesteBeitraege, useStand, useWuensche } from "../../api/hooks";
import { eigeneAenderungVergessen } from "../../api/startseite";
import type { Sprache } from "../../api/types";
import { setSprache, SPRACHEN, useT } from "../../lib/texte";
import { darfGeldSehen, istVerwaltung } from "../../lib/rechte";
import { datumKurz } from "../../lib/format";
import { load } from "../../lib/storage";
import { GESEHEN_SCHLUESSEL, hatUngelesen, neuestesDatum } from "../../screens/aktuelles/hilfen";
import { useTheme } from "../../lib/theme";
import { KARTE, karteVorladen, reiterAn } from "../../lib/wege";
import { useUi } from "../../store/ui";
import { useAuthStore } from "../../store/useAuthStore";

/**
 * Der kleine Punkt an einem Reiter: „hier liegt etwas für dich". Einmal
 * gezeichnet für Kopfleiste UND Mobilleiste — §36.8 verlangt am Telefon
 * DENSELBEN Punkt wie oben. Er sitzt in einer relativ gesetzten Hülle um das
 * Zeichen und ist für Vorleser stumm; was er bedeutet, trägt der Reiter selbst
 * (`aria-label`), sonst hörte man nur „Start".
 */
export function ReiterPunkt() {
  return (
    <span
      aria-hidden="true"
      style={{
        position: "absolute",
        top: -2,
        right: -3,
        width: 7,
        height: 7,
        borderRadius: "50%",
        background: "var(--accent-2)",
        boxShadow: "0 0 0 1.5px var(--panel-solid)",
      }}
    />
  );
}

/**
 * Der Ungelesen-Punkt an „Aktuelles" (§16.4) — EINE Rechnung für Kopfleiste und
 * Mobilleiste. Bis zum 01.10.2026 stand sie nur hier, und am Telefon, wo die
 * Reiter der Kopfleiste ausgeblendet sind, sah niemand den Punkt (Fehlerrunde
 * 01.10.2026, #36) — gerade dort, wo Feed und Forum am meisten gelesen werden.
 *
 * `Ich` trägt die Zahl laut Vertrag NICHT (SPEC §16.2: „der Client zählt aus
 * GET /aktuelles selbst"), also wird der jüngste Beitrag gegen den Merker im
 * Browser gehalten. Der Aufruf holt nur die erste Seite und höchstens alle zwei
 * Minuten; den Merker setzt der Bildschirm beim Öffnen. Beide Leisten teilen
 * sich denselben Abfrageschlüssel — ein zweiter Abruf entsteht nicht.
 */
export function useAktuellesUngelesen(angemeldet: boolean): boolean {
  const { pathname } = useLocation();
  const { data: neueste } = useNeuesteBeitraege(angemeldet);
  // Wer auf „Aktuelles" STEHT, liest gerade — dort kein Punkt (Fehlerrunde
  // 01.10.2026, #49). Der Bildschirm schreibt den Merker in einem Effekt NACH
  // dem Zeichnen; kam ein Beitrag live herein, blitzte der Punkt sonst am
  // eigenen Reiter auf, bis die Leiste das nächste Mal zeichnete.
  if (reiterAn("/aktuelles", pathname)) return false;
  return hatUngelesen(neuestesDatum(neueste?.beitraege ?? []), load<string | null>(GESEHEN_SCHLUESSEL, null));
}

/**
 * Was beim Ende einer Sitzung aus dem Speicher MUSS, bevor die nächste beginnt.
 *
 * Bis zum 01.10.2026 räumte nur das Abmelden im Kontomenü auf. Lief die Sitzung
 * dagegen ab (401 → „anonym") und meldete sich im selben Fenster jemand anderes
 * an, standen `/stand`, Projekte und Freigaben der VORIGEN Person im Cache —
 * samt noch nicht freigegebener Projekte (§36), bis zum nächsten Abruf
 * (Fehlerrunde 01.10.2026, #38). Darum rufen BEIDE Wege diese eine Funktion:
 * das Abmelden hier und die erfolgreiche Anmeldung (screens/Anmelden.tsx).
 *
 * Stehen bleibt nur, was niemandem gehört: `/konfig` und die öffentliche Karte
 * (`["oeffentlich", …]`). `["ich"]` geht ausdrücklich mit — sonst läse der
 * Profilriegel in App.tsx einen Wimpernschlag lang die vorige Person.
 */
export function sitzungVergessen(qc: QueryClient): void {
  const bleibt = new Set<unknown>([schluessel.konfig[0], schluessel.oeffentlichStand[0]]);
  qc.removeQueries({ predicate: (q) => !bleibt.has(q.queryKey[0]) });
  // Die Änderungen der vorigen Person ebenso — `qc.clear()` tat das bis dahin
  // beim Abmelden mit.
  qc.getMutationCache().clear();
  // Ebenso das Feierfenster der Abzeichen (api/startseite.ts): es gehörte der
  // Person, die gerade gegangen ist.
  eigeneAenderungVergessen();
}

export interface TopNavProps {
  /** Erzwingt die abgemeldete Fassung — die öffentliche Karte nutzt das. */
  oeffentlich?: boolean;
}

export function TopNav({ oeffentlich }: TopNavProps = {}) {
  const navigate = useNavigate();
  // Vor jedem frühen Ausstieg unten: welcher Reiter leuchtet, entscheidet der
  // Pfad (`reiterAn`, lib/wege.ts), nicht `NavLink`.
  const { pathname } = useLocation();
  const qc = useQueryClient();
  const status = useAuthStore((s) => s.status);
  const abmelden = useAuthStore((s) => s.abmelden);
  const angemeldet = status === "angemeldet" && !oeffentlich;

  const { t, sprache } = useT();
  const { thema, umschalten } = useTheme();
  const { data: ich } = useIch(angemeldet);
  const { data: stand } = useStand(angemeldet);
  const spracheAendern = useIchAendern();

  const suche = useUi((s) => s.filter.suche);
  const setFilter = useUi((s) => s.setFilter);
  const setBueroFarben = useUi((s) => s.setBueroFarben);
  const zeichnet = useUi((s) => s.zeichnen !== "aus");

  // Der Ungelesen-Punkt am Reiter „Aktuelles" (§16.4) — gerechnet in
  // `useAktuellesUngelesen`, weil die Mobilleiste denselben Punkt trägt.
  const ungelesen = useAktuellesUngelesen(angemeldet);

  // §36.8: die Freigaben neuer Projekte. Seit dem 29.09.2026 zählt `warten` nur,
  // was bei mir als PROJEKTLEITUNG des Projekts liegt — auch als GF oder Admin
  // (die dürfen jede Einreichung freigeben, werden aber nicht gerufen; der
  // Server rechnet das, hier wird nichts gefiltert). EINE Abfrage für den Punkt an „Start",
  // den Eintrag im Kontomenü, die Mobilleiste und die Karte auf der Startseite
  // (derselbe Schlüssel, TanStack fragt einmal). Anders als Wünsche und Runde
  // NICHT erst beim Öffnen des Menüs: der Punkt muss es vorher wissen. Frisch
  // hält sie der Ereignisstrom (`lib/ereignisse.ts`).
  //   zahl       was etwas von mir VERLANGT (`freigabenZahl`): Punkt und Zahl im Menü
  //   imMenue    ob der Eintrag überhaupt steht — auch für eine eigene, noch
  //              offene Einreichung, die nichts verlangt, deren Stand man aber
  //              nachsehen will
  const { data: freigaben } = useFreigaben(angemeldet);
  const freigabenN = freigabenZahl(freigaben);
  const freigabenImMenue = !!freigaben && freigaben.warten.length + freigaben.eigene.length > 0;

  const [kontoOffen, setKontoOffen] = useState(false);
  const { data: wuensche } = useWuensche(kontoOffen);
  // §18.5: die nächste Runde — ebenfalls erst beim Öffnen des Menüs geladen. Wer
  // in einem Büro ohne Rundentag sitzt, bekommt `null`; dann steht der Eintrag
  // gar nicht da, statt auf eine Seite zu führen, die nur „nichts" sagt.
  const { data: meineRunde } = useMeineRunde(kontoOffen);
  const inArbeit = wuensche?.filter((w) => w.status === "in_arbeit").length ?? 0;
  // Am Telefon hat die Leiste keinen Platz für ein stehendes Suchfeld: ein
  // Lupenknopf klappt es auf, und solange es offen ist, weicht die Marke.
  const [mobilSuche, setMobilSuche] = useState(false);
  // Die Trefferliste faellt beim Waehlen und bei Escape zu, NICHT beim Verlassen
  // des Feldes: ein `blur` kaeme vor dem Klick auf eine Zeile, und der Griff
  // ginge ins Leere. Geschlossen wird darum ueber einen Klick daneben.
  const [trefferOffen, setTrefferOffen] = useState(false);

  // Klick daneben schliesst die Trefferliste. Bewusst `mousedown` und nicht
  // `click`: ein Klick INNERHALB der Liste soll seine Zeile noch erreichen,
  // und `.suchfeld` umschliesst Feld und Liste gemeinsam.
  useEffect(() => {
    if (!trefferOffen) return;
    const daneben = (e: MouseEvent): void => {
      if (!(e.target as HTMLElement | null)?.closest(".suchfeld")) setTrefferOffen(false);
    };
    document.addEventListener("mousedown", daneben);
    return () => document.removeEventListener("mousedown", daneben);
  }, [trefferOffen]);
  const kontoRef = useRef<HTMLDivElement>(null);
  const sucheRef = useRef<HTMLInputElement>(null);
  const mobilSucheRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (mobilSuche) mobilSucheRef.current?.focus();
  }, [mobilSuche]);

  // Die Farbtafel für die Avatarringe. Sie steht hier und nicht in <Avatar>,
  // damit der Baustein keine eigene Abfrage braucht (store/ui.ts erklärt es);
  // `setBueroFarben` schreibt nur bei echter Änderung.
  useEffect(() => {
    if (!stand) return;
    const tafel: Record<string, string> = {};
    for (const b of stand.bueros) tafel[b.id] = b.farbe;
    setBueroFarben(tafel);
  }, [stand, setBueroFarben]);

  // Kontomenü schließen: Klick daneben oder Escape.
  useEffect(() => {
    if (!kontoOffen) return;
    const daneben = (e: MouseEvent) => {
      if (!kontoRef.current?.contains(e.target as Node)) setKontoOffen(false);
    };
    const taste = (e: KeyboardEvent) => e.key === "Escape" && setKontoOffen(false);
    document.addEventListener("mousedown", daneben);
    document.addEventListener("keydown", taste);
    return () => {
      document.removeEventListener("mousedown", daneben);
      document.removeEventListener("keydown", taste);
    };
  }, [kontoOffen]);

  // „/" springt ins Suchfeld — aber nur, wenn gerade nicht getippt wird.
  useEffect(() => {
    if (!angemeldet) return;
    const taste = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const ziel = e.target;
      if (ziel instanceof HTMLElement && (ziel.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(ziel.tagName))) return;
      e.preventDefault();
      sucheRef.current?.focus();
    };
    document.addEventListener("keydown", taste);
    return () => document.removeEventListener("keydown", taste);
  }, [angemeldet]);

  const spracheSetzen = (s: Sprache) => {
    if (s === sprache) return;
    setSprache(s);
    // Das Profil trägt die Sprache mit (SPEC §3: `PATCH /me`), damit sie auf
    // dem Telefon dieselbe ist wie am Schreibtisch. Schlägt der Aufruf fehl,
    // bleibt es bei der lokalen Wahl — ein Sprachschalter, der einen Toast
    // wirft, wäre lauter als der Anlass.
    if (angemeldet) spracheAendern.mutate({ sprache: s });
  };

  // Während eine Projektgrenze gezeichnet wird, ist der Themenschalter GESPERRT
  // (F3s Befund in docs/INTEGRATION.md): der Themenwechsel tauscht den
  // Kartenstil über `setStyle`, Terra Draw legt seine Ebenen aber unter einem
  // eigenen Präfix (`td-…`) an und nicht unter `lp-…` — die Regel aus SPEC §7,
  // die alle `lp-`-Ebenen wieder einhängt, trägt sie also nicht mit. Das
  // begonnene, noch nicht gesicherte Vieleck lebt zudem nur im Speicher des
  // Werkzeugs. Es wäre weg, und zwar lautlos. Zwei Minuten im dunklen Thema zu
  // zeichnen ist der billigere Preis.
  const themaKnopf = (
    <IconButton
      icon={thema === "dark" ? "sun" : "moon"}
      title={zeichnet ? t.nav.themaGesperrt : thema === "dark" ? t.nav.themaHell : t.nav.themaDunkel}
      box={36}
      size={17}
      disabled={zeichnet}
      onClick={umschalten}
    />
  );

  const sprachSchalter = (
    <div className="seg" role="group" aria-label={t.nav.sprache}>
      {SPRACHEN.map((s) => (
        <button key={s} type="button" className={s === sprache ? "active" : ""} aria-pressed={s === sprache} onClick={() => spracheSetzen(s)}>
          {s.toUpperCase()}
        </button>
      ))}
    </div>
  );

  /* ---------------------------------------------------- abgemeldete Fassung */

  if (!angemeldet) {
    return (
      <header className="kopf glass">
        <Logo />
        <div style={{ flex: 1 }} />
        {sprachSchalter}
        {themaKnopf}
        <Button variant="primary" icon="sign-in" onClick={() => navigate("/anmelden")}>
          {t.nav.anmelden}
        </Button>
      </header>
    );
  }

  /* --------------------------------------------------- angemeldete Fassung */

  // „Büros" hat keine eigene Übersichtsseite (SPEC §7 kennt nur
  // `/bueros/:id`); der Reiter führt auf das erste Büro der Sortierung. Ohne
  // Büros — das gibt es nur vor der ersten Migration — fällt er weg.
  const erstesBuero = stand?.bueros[0]?.id;

  // §32: „Start" vorn — die Startseite ist, wo man ankommt; „Karte" gleich
  // dahinter, sie war bis dahin der erste Reiter. `genau` bei „Start", sonst
  // leuchtete „/" auf jeder Seite.
  // `hinweis`: was der Punkt sagt — als Titel beim Zeigen und für Vorleser im Namen des Reiters.
  const punkte: { zu: string; text: string; icon: string; genau?: boolean; punkt?: boolean; hinweis?: string }[] = [
    // §36.8: der Punkt an „Start", sobald eine Freigabe etwas von mir verlangt —
    // die Karte dazu steht auf der Startseite.
    {
      zu: "/",
      text: t.nav.start,
      icon: "house",
      genau: true,
      punkt: freigabenN > 0,
      hinweis: freigabenN > 0 ? t.freigabe.punkt(freigabenN) : undefined,
    },
    { zu: KARTE, text: t.nav.karte, icon: "map" },
    { zu: "/projekte", text: t.nav.projekte, icon: "squares-four" },
    { zu: "/abgaben", text: t.nav.abgaben, icon: "calendar" },
    { zu: "/team", text: t.nav.team, icon: "users" },
    // §16.4: „Aktuelles" steht zwischen Team und Büros. Die Glocke, weil es
    // in der Registry kein Megafon gibt — und weil dieselbe Glocke schon für
    // die Abgaben-Erinnerung steht, ist sie hier nicht fremd.
    {
      zu: "/aktuelles",
      text: t.nav.aktuelles,
      icon: "bell",
      punkt: ungelesen,
      hinweis: ungelesen ? t.nav.aktuellesUngelesen : undefined,
    },
    // §34: „Zeitung" und „Links" nach „Aktuelles", vor „Büros" — die Zeitung ist
    // die Wochenausgabe dessen, was in Aktuelles einzeln steht, und gehört daneben.
    // Am Telefon stehen beide im Kontomenü (unten), nicht in der Mobilleiste.
    { zu: "/zeitung", text: t.nav.zeitung, icon: "newspaper" },
    { zu: "/links", text: t.nav.links, icon: "link" },
    ...(erstesBuero ? [{ zu: `/bueros/${erstesBuero}`, text: t.nav.bueros, icon: "buildings" }] : []),
  ];

  const person = ich ?? null;

  return (
    <header className="kopf glass">
      {mobilSuche ? (
        <div className="suchfeld suchfeld-mobil">
          <Icon name="magnifying-glass" size={16} />
          <input
            ref={mobilSucheRef}
            type="search"
            value={suche}
            placeholder={t.nav.suchePlatzhalter}
            aria-label={t.nav.suchePlatzhalter}
            onChange={(e) => {
              setFilter({ suche: e.target.value });
              setTrefferOffen(true);
            }}
            onFocus={() => setTrefferOffen(true)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                if (trefferOffen) {
                  setTrefferOffen(false);
                } else {
                  setFilter({ suche: "" });
                  setMobilSuche(false);
                }
              }
              // Am Telefon ist Enter das Zeichen „fertig getippt" — die Liste
              // bleibt stehen, damit der Daumen sie treffen kann.
              if (e.key === "Enter") e.currentTarget.blur();
            }}
          />
          <IconButton
            icon="x"
            title={t.allgemein.schliessen}
            box={28}
            size={14}
            onClick={() => {
              setFilter({ suche: "" });
              setTrefferOffen(false);
              setMobilSuche(false);
            }}
          />
          <Suchliste suche={suche} offen={trefferOffen} mobil onSchliessen={() => setTrefferOffen(false)} />
        </div>
      ) : (
        <Logo />
      )}

      <nav className="kopf-nav mobile-hide" aria-label={t.nav.bereiche}>
        {punkte.map((p) => {
          // `Link` statt `NavLink`: auf `/projekte/:id` leuchtet „Karte" und
          // NICHT „Projekte" — und `aria-current` muss dasselbe sagen wie das
          // Bild (lib/wege.ts, `reiterAn`).
          const an = reiterAn(p.zu, pathname, p.genau);
          // Wer auf „Karte" zeigt, klickt gleich: das Kartenbündel schon jetzt.
          const vorladen = p.zu === KARTE ? karteVorladen : undefined;
          return (
            <Link
              key={p.zu}
              to={p.zu}
              title={p.hinweis ? `${p.text} – ${p.hinweis}` : p.text}
              aria-label={p.hinweis ? `${p.text}, ${p.hinweis}` : undefined}
              aria-current={an ? "page" : undefined}
              className={`navlink ring-focus${an ? " aktiv" : ""}`}
              onPointerEnter={vorladen}
              onFocus={vorladen}
            >
              <span style={{ position: "relative", display: "inline-flex" }}>
                <Icon name={p.icon} size={17} />
                {p.punkt && <ReiterPunkt />}
              </span>
              <span className="navlink-text">{p.text}</span>
            </Link>
          );
        })}
      </nav>

      {/* Der Fuellraum zwischen Reitern und Suchfeld — am Telefon nur, solange
          das Feld GESCHLOSSEN ist. Offen konkurrierte er sonst als zweites
          `flex:1` mit dem Suchfeld (`.suchfeld-mobil`) um denselben Platz und
          teilte ihn hälftig — das Feld blieb halb so breit wie möglich. */}
      {!mobilSuche && <div style={{ flex: 1 }} />}

      <div className="suchfeld mobile-hide">
        <Icon name="magnifying-glass" size={16} />
        <input
          ref={sucheRef}
          type="search"
          value={suche}
          placeholder={t.nav.suchePlatzhalter}
          aria-label={t.nav.suchePlatzhalter}
          onChange={(e) => {
            setFilter({ suche: e.target.value });
            setTrefferOffen(true);
          }}
          onFocus={() => setTrefferOffen(true)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              // Erst die Liste, dann das Feld: wer einmal Escape drueckt, will
              // die Liste weg und seinen Filter behalten.
              if (trefferOffen) setTrefferOffen(false);
              else {
                setFilter({ suche: "" });
                e.currentTarget.blur();
              }
            }
          }}
        />
        <kbd>/</kbd>
        {/* Fehlerrunde 01.10.2026, #16: diese Liste ist am Telefon nur per CSS
            versteckt (`.mobile-hide`), bleibt aber eingehängt — und ihr
            Fensterhörer nahm Enter als „erste Zeile wählen". Wer in der
            Telefonsuche auf „Suchen" tippte, sprang sofort zum ersten Treffer,
            statt die Liste für den Daumen stehen zu sehen. Solange die
            Telefonsuche offen ist (das geht nur unter 860 px), bleibt sie zu. */}
        <Suchliste suche={suche} offen={trefferOffen && !mobilSuche} onSchliessen={() => setTrefferOffen(false)} />
      </div>

      <div className="mobile-hide">{sprachSchalter}</div>
      {!mobilSuche && (
        <IconButton
          className="mobile-only"
          icon="magnifying-glass"
          title={t.nav.suchePlatzhalter}
          box={36}
          size={17}
          active={suche.trim().length > 0}
          onClick={() => setMobilSuche(true)}
        />
      )}
      {themaKnopf}

      <div ref={kontoRef} style={{ position: "relative", flex: "none" }}>
        {person ? (
          <Avatar person={person} size={32} ring title={t.nav.konto} onClick={() => setKontoOffen((o) => !o)} />
        ) : (
          <IconButton icon="user" title={t.nav.konto} onClick={() => setKontoOffen((o) => !o)} />
        )}
        {kontoOffen && (
          <div className="konto-menue anim-pop" role="menu">
            {person && (
              <div className="kopfzeile">
                <div style={{ fontWeight: 600 }}>{person.anzeigename ?? person.email}</div>
                <div className="hinweiszeile">{t.rolle[person.rolle]}</div>
              </div>
            )}
            {/* §36.8: die Freigaben ganz oben — der einzige Eintrag hier, der Arbeit
                meldet, und er steht nur, solange es etwas gibt. Er führt auf die
                Karte der Startseite (`Start` scrollt bei `#freigaben`, der Kennung
                `FREIGABEN_ANKER` aus screens/start — hier als Zeichenkette, damit die
                Kopfleiste nicht das Bündel der Startseite nachzieht); eine eigene
                Seite gibt es nicht, entschieden wird im Projektmodal. Die Zahl
                zählt nur, was etwas verlangt — wie der Punkt an „Start". */}
            {freigabenImMenue && (
              <Link to="/#freigaben" role="menuitem" onClick={() => setKontoOffen(false)}>
                <Icon name="seal-check" size={16} />
                {t.freigabe.menue}
                {freigabenN > 0 && (
                  <span className="menue-zahl" title={t.freigabe.punkt(freigabenN)}>
                    {freigabenN}
                  </span>
                )}
              </Link>
            )}
            <Link to="/profil" role="menuitem" onClick={() => setKontoOffen(false)}>
              <Icon name="user" size={16} />
              {t.nav.profil}
            </Link>
            {/* §21: der Weg zu „Ich wünsche mir eine Funktion". Er steht hier und
                nicht in der Leiste — man geht selten hin, und sieben Reiter sind
                genug. Der Stern ist dasselbe Zeichen wie am Meilenstein; hier
                steht er für „hätte ich gern". */}
            <Link to="/wuensche" role="menuitem" onClick={() => setKontoOffen(false)}>
              <Icon name="star" size={16} />
              {t.wunsch.kurz}
              {/* §25: was gerade gebaut wird, schon hier — die Liste wird erst
                  geladen, wenn das Menü offen ist. */}
              {inArbeit > 0 && <span className="menue-zahl">{t.wunsch.imMenue(inArbeit)}</span>}
            </Link>
            {/* §34: am Telefon wandern „Zeitung" und „Links" hierher — die
                Mobilleiste bleibt bei ihren sechs Einträgen (Entscheidung vom
                28.09.; jeder weitere machte dort jedes Ziel schmaler). Nur
                `mobile-only`: am Schreibtisch stehen beide als Reiter oben,
                doppelt wären sie Rauschen. Der alte Eintrag „Dienste" geht in
                „Links" auf (`/dienste` leitet dorthin um). */}
            <Link to="/zeitung" role="menuitem" className="mobile-only" onClick={() => setKontoOffen(false)}>
              <Icon name="newspaper" size={16} />
              {t.nav.zeitung}
            </Link>
            <Link to="/links" role="menuitem" className="mobile-only" onClick={() => setKontoOffen(false)}>
              <Icon name="link" size={16} />
              {t.nav.links}
            </Link>
            {meineRunde && (
              <Link to="/runde" role="menuitem" onClick={() => setKontoOffen(false)}>
                <Icon name="calendar" size={16} />
                {t.nav.runde}
                <span className="menue-zahl">{datumKurz(meineRunde.datum, sprache)}</span>
              </Link>
            )}
            {/* §18.6: das Blatt der Geschäftsführung steht im Kontomenü und nicht
                in der Leiste — es sieht nur eine Handvoll Leute, und eine Leiste,
                die je nach Rolle anders lang ist, verwirrt die anderen. */}
            {darfGeldSehen(person?.rolle) && (
              <Link to="/leitung" role="menuitem" onClick={() => setKontoOffen(false)}>
                <Icon name="board" size={16} />
                {t.nav.leitung}
              </Link>
            )}
            {istVerwaltung(person?.rolle) && (
              <Link to="/admin" role="menuitem" onClick={() => setKontoOffen(false)}>
                <Icon name="sliders" size={16} />
                {t.nav.verwaltung}
              </Link>
            )}
            {/* Der Sprachschalter steckt auf dem Telefon hier, weil in der
                Leiste kein Platz dafür ist. */}
            <div className="mobile-only" style={{ padding: "8px 10px" }}>
              {sprachSchalter}
            </div>
            <button
              type="button"
              role="menuitem"
              className="gefahr"
              onClick={() => {
                setKontoOffen(false);
                void abmelden().then(() => {
                  // Der Zwischenspeicher MUSS leer sein, bevor der Nächste
                  // sich anmeldet: sonst sähe er einen Wimpernschlag lang das
                  // `/stand` der vorigen Person aus dem Cache. Die Anmeldung
                  // räumt seit #38 noch einmal auf — für den Fall, dass die
                  // Sitzung abläuft, statt abgemeldet zu werden.
                  sitzungVergessen(qc);
                  navigate("/", { replace: true });
                });
              }}
            >
              <Icon name="sign-out" size={16} />
              {t.nav.abmelden}
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
